import React, {useEffect, useRef, useState} from 'react';
import {Box, Button, Card, Container, Grid, IconButton, Stack, Typography} from '@mui/material';
import {SubmitHandler, useForm} from 'react-hook-form';
import {Form, FormTextField, RichTextInput} from 'components/form';
import {useModal} from 'hooks';
import {TagsSearch} from './TagsSearch';
import {useStoryControllerCreate, useStoryControllerFindOne, useStoryControllerUpdateDraft} from 'api/story';
import {useNavigate, useParams} from 'react-router-dom';
import {HelpIcon} from 'assets/icons';
import {Labeled} from 'components/form/Labeled';
import {CustomModal} from 'components';
import RulesImg from 'assets/images/rules.jpg';
import {LoadingButton} from '@mui/lab';
import {ValidationError} from 'yup';
import {formatNumber, ROUTES, STORY_DESCRIPTION_MIN_LENGTH, STORY_TITLE_MAX_LENGTH} from 'utils';
import {CreateStoryPublicDtoIsDraft, Tag} from 'api/mDDPublic.schemas';
import {useQueryClient} from '@tanstack/react-query';
import {getUserControllerFindAllUserStoriesQueryKey} from 'api/user';
import moment from 'moment';
import {Editor} from '@tiptap/react';
import {AddPhotoButton} from './AddPhotoButton';
import {schema} from './schema';
import {VideoButton} from './VideoButton';

type FormData = {
    tags: Tag[];
    title: string;
    description: string;
    photo: any;
    video: any;
};

const MIN_STORY_TEXT_ERROR = `Текст истории должен содержать не менее ${STORY_DESCRIPTION_MIN_LENGTH} символов`;

export const CreateStory = () => {
    const editorRef = useRef<{editor: Editor}>();
    const queryClient = useQueryClient();

    const {storyId: storyIdParam} = useParams();
    const [storyId, setStoryId] = useState(storyIdParam);
    const form = useForm<FormData>();
    const {handleSubmit, clearErrors, setError, reset, getValues} = form;

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const {open: openRulesModal, handleToggle: handleToggleRulesModal} = useModal();
    const {open: openSuccessModal, handleToggle: handleToggleSuccessModal} = useModal();
    const [isMinLengthToastOpen, setIsMinLengthToastOpen] = useState(false);
    const [text, setText] = useState('');
    const navigate = useNavigate();

    const createStoryMutation = useStoryControllerCreate();
    const createStoryAutoSaveMutation = useStoryControllerCreate();
    const updateDraftMutation = useStoryControllerUpdateDraft();
    const updateDraftAutoSaveMutation = useStoryControllerUpdateDraft();
    const {data: story} = useStoryControllerFindOne(storyIdParam!, {query: {enabled: !!storyIdParam}});

    useEffect(() => {
        if (story) {
            editorRef?.current?.editor?.commands.setContent(story.description);
            reset({
                tags: story.tags.map((i) => i.tag),
                title: story.title,
                description: story.description,
                photo: story.file ? {file: story.file} : undefined,
                video: story.videoFile ? {file: story.videoFile} : null
            });
        }
    }, [story]);

    useEffect(() => {
        let interval: NodeJS.Timeout;
        if (!storyId) {
            createStoryAutoSaveMutation
                .mutateAsync({
                    data: {...getData(getValues(), true), uploadedFile: undefined, uploadedVideoFile: undefined }
                })
                .then((res) => setStoryId(res.id.toString()));
        } else {
            interval = setInterval(() => {
                updateDraftAutoSaveMutation.mutateAsync({
                    id: storyId!,
                    data: {...getData(getValues(), true), uploadedFile: undefined, uploadedVideoFile: undefined}
                });
            }, 10000);
        }

        return () => clearInterval(interval);
    }, [storyId]);

    useEffect(() => {
        if (!isMinLengthToastOpen) {
            return;
        }

        const timeout = setTimeout(() => setIsMinLengthToastOpen(false), 3000);

        return () => clearTimeout(timeout);
    }, [isMinLengthToastOpen]);

    const getData = (data: FormData, isDraft: boolean) => ({
        title: data?.title,
        description: editorRef?.current?.editor?.getHTML() as string,
        fileId: data.photo?.file?.id,
        uploadedFile: !data.photo?.file?.url ? data.photo : undefined,
        tagIds: data?.tags?.map((el: Tag) => el.id.toString()),
        isDraft: isDraft.toString() as CreateStoryPublicDtoIsDraft,
        videoFileId: data.video?.file?.id,
        uploadedVideoFile: !data.video?.file?.url && data.video ? data.video : undefined,
    });

    const createStory = (data: FormData, isDraft: boolean) => {
        handleToggleSuccessModal();
        timeoutRef.current = setTimeout(() => {
            const query = storyId ? updateDraftMutation : createStoryMutation;
            query
                .mutateAsync({
                    id: storyId!,
                    data: getData(data, isDraft)
                })
                .then(() => {
                    handleToggleSuccessModal();
                    queryClient.refetchQueries(getUserControllerFindAllUserStoriesQueryKey()).then(() => navigate(ROUTES.PROFILE));
                });
        }, 2000);
    };

    const onStorySubmit: SubmitHandler<FormData> = (data) => {
        const storyDescriptionText = editorRef?.current?.editor?.getText().trim() || '';

        schema
            .validate({...data, description: storyDescriptionText}, {abortEarly: false})
            .then(() => {
                createStory(data, false);
                setText('Ваша история отправлена на модерацию.');
            })
            .catch((err) => {
                const validationErrors = err.inner as ValidationError[];
                const hasOnlyDescriptionError = validationErrors.every((e) => e.path === 'description');

                if (hasOnlyDescriptionError && storyDescriptionText.length > 0 && storyDescriptionText.length < STORY_DESCRIPTION_MIN_LENGTH) {
                    setIsMinLengthToastOpen(true);
                }

                validationErrors.forEach((e: ValidationError) =>
                    setError(e.path as keyof FormData, {
                        type: e.type,
                        message: e.message
                    })
                );
            });
    };

    const onDraftSubmit: SubmitHandler<FormData> = (data) => {
        const remained = moment(story?.createdAt).add(8, 'd').diff(moment(), 'days');
        setText(`История будет храниться в черновиках ${story?.createdAt ? 'ещё ' + formatNumber(remained, ['день', 'дня', 'дней']) : '7 дней'}.`);
        createStory(data, true);
    };

    return (
        <>
            <Container>
                <Box sx={{display: 'flex', mt: 7.5, mb: 6, flexWrap: 'wrap'}}>
                    <Typography variant='h1' color='secondary'>
                        Доброе&nbsp;
                    </Typography>
                    <Typography variant='h1' color='primary'>
                        Дело
                    </Typography>
                </Box>
                <Form form={form}>
                    <Card
                        sx={{
                            overflow: 'visible',
                            padding: 3.75
                        }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} lg={4} sx={{display: 'flex', flexDirection: 'column'}}>
                                <AddPhotoButton />
                                <VideoButton />

                                <TagsSearch />
                            </Grid>
                            <Grid item xs={12} lg={8} sx={{gap: {xs: 3, md: 6}, display: 'flex', flexDirection: 'column'}}>
                                <Labeled
                                    label='Название истории'
                                    tooltip={
                                        <IconButton onClick={handleToggleRulesModal} sx={{my: '-13px'}}>
                                            <HelpIcon />
                                        </IconButton>
                                    }>
                                    <FormTextField name='title' placeholder={' '} maxLength={STORY_TITLE_MAX_LENGTH} />
                                </Labeled>
                                <RichTextInput ref={editorRef} name='description' />
                            </Grid>
                        </Grid>
                        <Stack direction={{md: 'row'}} gap={{xs: 2, md: 3}} sx={{justifyContent: 'flex-end', mt: 3}}>
                            <Button
                                variant='contained'
                                color='neutral'
                                onClick={() => navigate(-1)}
                                sx={{
                                    height: 'auto',
                                    p: '14px 80px'
                                }}>
                                Отменить
                            </Button>
                            <LoadingButton
                                loading={updateDraftMutation.isLoading}
                                type='submit'
                                variant='contained'
                                color='neutral'
                                onClick={() => {
                                    clearErrors();
                                    handleSubmit(onDraftSubmit)();
                                }}
                                sx={{
                                    height: 'auto',
                                    p: '14px 80px'
                                }}>
                                В черновик
                            </LoadingButton>
                            <LoadingButton
                                loading={createStoryMutation.isLoading}
                                type='submit'
                                variant='contained'
                                onClick={() => {
                                    clearErrors();
                                    handleSubmit(onStorySubmit)();
                                }}
                                sx={{p: '14px 80px'}}>
                                Отправить
                            </LoadingButton>
                        </Stack>
                    </Card>
                </Form>
            </Container>

            <CustomModal maxWidth='sm' scroll='body' open={openRulesModal} onClose={handleToggleRulesModal}>
                <Box component='img' sx={{width: '100%', p: 2}} src={RulesImg} />
            </CustomModal>
            <CustomModal
                open={openSuccessModal}
                onClose={handleToggleSuccessModal}
                hiddenCloseIcon
                sx={{'.MuiDialogContent-root': {p: 2}, '*': {color: '#2c2a29'}}}
                fullWidth={false}>
                {text}
                <Button
                    color='neutral'
                    sx={{py: 1, ml: 2}}
                    onClick={() => {
                        clearTimeout(timeoutRef.current!);
                        handleToggleSuccessModal();
                    }}>
                    Отменить
                </Button>
            </CustomModal>
            <CustomModal
                open={isMinLengthToastOpen}
                onClose={() => setIsMinLengthToastOpen(false)}
                hiddenCloseIcon
                sx={{'.MuiDialogContent-root': {p: 2}, '*': {color: '#2c2a29'}}}
                fullWidth={false}>
                {MIN_STORY_TEXT_ERROR}
            </CustomModal>
        </>
    );
};
